import React, { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Skeleton,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Tooltip,
  Divider,
  FormGroup,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { agencyApi } from '../services/api';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [subaccounts, setSubaccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'admin',
    hasAllAccess: false,
    subaccountAccess: [],
    isActive: true,
  });
  const [accessData, setAccessData] = useState({
    hasAllAccess: false,
    subaccountIds: [],
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, subaccountsRes] = await Promise.all([
        agencyApi.getUsers(),
        agencyApi.getSubaccounts(),
      ]);

      if (usersRes.data.success) {
        setUsers(usersRes.data.users || []);
      }
      if (subaccountsRes.data.success) {
        setSubaccounts(subaccountsRes.data.subaccounts || []);
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      // Edit mode
      setEditMode(true);
      setSelectedUser(user);
      setFormData({
        email: user.email || '',
        password: '',
        name: user.name || '',
        role: user.role || 'admin',
        hasAllAccess: user.has_all_access || false,
        subaccountAccess: [],
        isActive: user.is_active !== false, // Default to true if null/undefined
      });
    } else {
      // Add mode
      setEditMode(false);
      setSelectedUser(null);
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'admin',
        hasAllAccess: false,
        subaccountAccess: [],
        isActive: true,
      });
    }
    setError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError('');
    setEditMode(false);
    setSelectedUser(null);
  };

  const handleOpenAccessDialog = async (user) => {
    setSelectedUser(user);
    setFormLoading(true);

    try {
      const response = await agencyApi.getUserAccess(user.id);
      if (response.data.success) {
        setAccessData({
          hasAllAccess: response.data.hasAllAccess,
          subaccountIds: response.data.accessibleTenantIds || [],
        });
      }
    } catch (error) {
      logger.error('Error fetching user access:', error);
      setAccessData({
        hasAllAccess: user.has_all_access || false,
        subaccountIds: [],
      });
    } finally {
      setFormLoading(false);
    }

    setAccessDialogOpen(true);
  };

  const handleCloseAccessDialog = () => {
    setAccessDialogOpen(false);
    setSelectedUser(null);
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleSubaccountToggle = (subaccountId) => {
    setFormData((prev) => {
      const currentAccess = prev.subaccountAccess || [];
      if (currentAccess.includes(subaccountId)) {
        return {
          ...prev,
          subaccountAccess: currentAccess.filter((id) => id !== subaccountId),
        };
      } else {
        return {
          ...prev,
          subaccountAccess: [...currentAccess, subaccountId],
        };
      }
    });
  };

  const handleAccessSubaccountToggle = (subaccountId) => {
    setAccessData((prev) => {
      const currentIds = prev.subaccountIds || [];
      if (currentIds.includes(subaccountId)) {
        return {
          ...prev,
          subaccountIds: currentIds.filter((id) => id !== subaccountId),
        };
      } else {
        return {
          ...prev,
          subaccountIds: [...currentIds, subaccountId],
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    // Validate
    if (!editMode && (!formData.email || !formData.password)) {
      setError('Email and password are required');
      setFormLoading(false);
      return;
    }

    if (!editMode && formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setFormLoading(false);
      return;
    }

    try {
      let response;
      if (editMode) {
        const updateData = {
          name: formData.name,
          role: formData.role,
          hasAllAccess: formData.hasAllAccess,
          isActive: formData.isActive,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        if (!formData.hasAllAccess) {
          updateData.subaccountAccess = formData.subaccountAccess;
        }
        response = await agencyApi.updateUser(selectedUser.id, updateData);
      } else {
        response = await agencyApi.addUser(formData);
      }

      if (response.data.success) {
        toast.success(editMode ? 'User updated successfully!' : 'User added successfully!');
        fetchData();
        handleCloseDialog();
      } else {
        setError(response.data.message || 'Failed to save user');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveAccess = async () => {
    setFormLoading(true);

    try {
      const response = await agencyApi.updateUserAccess(selectedUser.id, {
        hasAllAccess: accessData.hasAllAccess,
        subaccountIds: accessData.hasAllAccess ? [] : accessData.subaccountIds,
      });

      if (response.data.success) {
        toast.success('Access updated successfully!');
        fetchData();
        handleCloseAccessDialog();
      } else {
        toast.error(response.data.message || 'Failed to update access');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setFormLoading(true);

    try {
      const response = await agencyApi.deleteUser(selectedUser.id);

      if (response.data.success) {
        toast.success('User deleted successfully!');
        setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
        handleCloseDeleteDialog();
      } else {
        toast.error(response.data.message || 'Failed to delete user');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'primary';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isOwner = currentUser?.role === 'owner';

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Team Members
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            Manage users who can access your agency dashboard and subaccounts
          </Typography>
        </Box>
        {isOwner && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add User
          </Button>
        )}
      </Box>

      {/* Users Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Access</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                {isOwner && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={150} /></TableCell>
                    <TableCell><Skeleton width={200} /></TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    {isOwner && <TableCell><Skeleton width={80} /></TableCell>}
                  </TableRow>
                ))
              ) : users.length > 0 ? (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: user.role === 'owner' ? 'primary.main' : 'secondary.main',
                            width: 36,
                            height: 36,
                          }}
                        >
                          {user.name?.charAt(0) || user.email?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.name || 'No name'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        size="small"
                        color={getRoleColor(user.role)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {user.role === 'owner' || user.has_all_access ? (
                        <Chip
                          label="All Subaccounts"
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<SecurityIcon fontSize="small" />}
                        />
                      ) : (
                        <Chip
                          label="Limited Access"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={user.is_active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {formatDate(user.last_login)}
                      </Typography>
                    </TableCell>
                    {isOwner && (
                      <TableCell>
                        {user.role !== 'owner' && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Manage Access">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAccessDialog(user)}
                                sx={{ color: '#06AB8B' }}
                              >
                                <SecurityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit User">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(user)}
                                sx={{ color: '#3b82f6' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete User">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(user)}
                                sx={{ color: '#dc2626' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isOwner ? 7 : 6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      No team members found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Edit Team Member' : 'Add Team Member'}
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required={!editMode}
                  disabled={editMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={editMode ? 'New Password (leave blank to keep current)' : 'Password'}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required={!editMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    label="Role"
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="member">Member</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {editMode && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Active Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: formData.isActive ? '#06AB8B' : '#666' }}>
                          {formData.isActive ? 'User can access the dashboard' : 'User cannot log in'}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  Subaccount Access
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hasAllAccess}
                      onChange={(e) => setFormData((prev) => ({ ...prev, hasAllAccess: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Grant access to all subaccounts"
                />
              </Grid>

              {!formData.hasAllAccess && subaccounts.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Select which subaccounts this user can access:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                    <FormGroup>
                      {subaccounts.map((sub) => (
                        <FormControlLabel
                          key={sub.id}
                          control={
                            <Checkbox
                              checked={formData.subaccountAccess.includes(sub.id)}
                              onChange={() => handleSubaccountToggle(sub.id)}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon fontSize="small" sx={{ color: '#666' }} />
                              <span>{sub.name}</span>
                              <Chip label={sub.slug} size="small" variant="outlined" sx={{ ml: 1 }} />
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formLoading}
            >
              {formLoading ? 'Saving...' : editMode ? 'Save Changes' : 'Add User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Manage Access Dialog */}
      <Dialog
        open={accessDialogOpen}
        onClose={handleCloseAccessDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ color: '#06AB8B' }} />
            Manage Subaccount Access
          </Box>
          <IconButton onClick={handleCloseAccessDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      {selectedUser.name?.charAt(0) || selectedUser.email?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {selectedUser.name || selectedUser.email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {selectedUser.email}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <FormControlLabel
                control={
                  <Switch
                    checked={accessData.hasAllAccess}
                    onChange={(e) => setAccessData((prev) => ({ ...prev, hasAllAccess: e.target.checked }))}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Grant access to all subaccounts
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      User will have access to all current and future subaccounts
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              {!accessData.hasAllAccess && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Select Subaccounts
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Choose which subaccounts this user can access and manage:
                  </Typography>

                  {formLoading ? (
                    <Box sx={{ py: 2 }}>
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} height={40} sx={{ mb: 1 }} />
                      ))}
                    </Box>
                  ) : subaccounts.length > 0 ? (
                    <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      {subaccounts.map((sub) => (
                        <Box
                          key={sub.id}
                          sx={{
                            p: 1.5,
                            borderBottom: '1px solid #f0f0f0',
                            '&:last-child': { borderBottom: 'none' },
                            '&:hover': { bgcolor: '#f8f9fa' },
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={accessData.subaccountIds.includes(sub.id)}
                                onChange={() => handleAccessSubaccountToggle(sub.id)}
                              />
                            }
                            label={
                              <Box sx={{ ml: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {sub.name}
                                  </Typography>
                                  <Chip label={sub.slug} size="small" variant="outlined" />
                                </Box>
                                {sub.custom_domain && (
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    {sub.custom_domain}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ m: 0, width: '100%' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="info">
                      No subaccounts found. Create subaccounts first to assign access.
                    </Alert>
                  )}

                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#0369a1' }}>
                      <strong>Selected:</strong> {accessData.subaccountIds.length} of {subaccounts.length} subaccounts
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAccessDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAccess}
            disabled={formLoading}
            startIcon={<SecurityIcon />}
          >
            {formLoading ? 'Saving...' : 'Save Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#dc2626' }}>
          Delete Team Member
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete this team member?
              </Typography>
              <Card variant="outlined" sx={{ bgcolor: '#fef2f2' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#dc2626' }}>
                      {selectedUser.name?.charAt(0) || selectedUser.email?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {selectedUser.name || 'No name'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {selectedUser.email}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              <Alert severity="warning" sx={{ mt: 2 }}>
                This action cannot be undone. The user will lose all access to the agency dashboard.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={formLoading}
            startIcon={<DeleteIcon />}
          >
            {formLoading ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
